import { useState, type ReactNode } from 'react';
import {
  BookOpen,
  Buildings,
  DotsThreeCircle,
  Lightbulb,
  Plus,
  Stack,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import {
  ADDITIONAL_MATERIAL_LIMITS,
  ADDITIONAL_MATERIAL_TYPES,
  createAdditionalMaterialDraft,
  type AdditionalMaterialDraft,
  type AdditionalMaterialType,
} from '@/lib/sessions/additionalMaterials';
import { cn } from '@/lib/utils/cn';
import { FileDropZone, Label, SourceToggle } from './MaterialFields';

const TYPE_ICONS: Record<AdditionalMaterialType, ReactNode> = {
  company_info: <Buildings className="h-3.5 w-3.5" />,
  study_material: <BookOpen className="h-3.5 w-3.5" />,
  interview_tips: <Lightbulb className="h-3.5 w-3.5" />,
  custom: <DotsThreeCircle className="h-3.5 w-3.5" />,
};

const { maxItems, maxFileBytes, maxTextChars } = ADDITIONAL_MATERIAL_LIMITS;

interface Props {
  items: AdditionalMaterialDraft[];
  onChange: (items: AdditionalMaterialDraft[]) => void;
}

export function AdditionalMaterialsSection({ items, onChange }: Props) {
  // Oversized picks never reach the draft, so their error lives here
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const atLimit = items.length >= maxItems;

  function update(id: string, patch: Partial<AdditionalMaterialDraft>) {
    onChange(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  function setFileError(id: string, message: string | null) {
    setFileErrors((prev) => {
      const next = { ...prev };
      if (message) next[id] = message;
      else delete next[id];
      return next;
    });
  }

  function onFile(id: string, file: File | null) {
    if (file && file.size > maxFileBytes) {
      setFileError(id, `"${file.name}" is over 10 MB. Pick a smaller file or paste the text.`);
      return;
    }
    setFileError(id, null);
    update(id, { file });
  }

  function remove(id: string) {
    setFileError(id, null);
    onChange(items.filter((item) => item.id !== id));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Label icon={<Stack className="h-3.5 w-3.5" />}>
          Additional materials
          <span className="normal-case tracking-normal text-muted-foreground/70">(optional)</span>
        </Label>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-[11px] tabular-nums',
            atLimit
              ? 'border-neurax-amber/30 bg-neurax-amber/10 text-neurax-amber'
              : 'border-white/10 bg-white/[0.04] text-muted-foreground'
          )}
        >
          {items.length} / {maxItems}
        </span>
      </div>
      <p className="-mt-1 text-[11px] text-muted-foreground">
        Company research, study guides or interview tips. Each one gets its own section in the AI
        prep prompt.
      </p>

      {items.map((item, index) => {
        const typeMeta = ADDITIONAL_MATERIAL_TYPES.find((t) => t.value === item.type);
        const itemLabel = `additional material ${index + 1}`;
        const fileError = fileErrors[item.id];
        return (
          <div
            key={item.id}
            data-testid={`additional-material-${index}`}
            className="space-y-3 rounded-xl border border-white/10 bg-white/[0.02] p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div
                role="radiogroup"
                aria-label={`Type for ${itemLabel}`}
                className="flex flex-wrap gap-1.5"
              >
                {ADDITIONAL_MATERIAL_TYPES.map((t) => {
                  const selected = item.type === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => update(item.id, { type: t.value })}
                      className={cn(
                        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition',
                        selected
                          ? 'border-neurax-electric/40 bg-neurax-electric/10 text-neurax-electric'
                          : 'border-white/10 bg-white/[0.02] text-muted-foreground hover:border-white/20 hover:text-white'
                      )}
                    >
                      {TYPE_ICONS[t.value]}
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => remove(item.id)}
                aria-label={`Remove ${itemLabel}`}
                className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={item.title}
                onChange={(e) => update(item.id, { title: e.target.value })}
                aria-label={`Name for ${itemLabel} (optional)`}
                placeholder="Name (optional)"
                title={
                  item.mode === 'file'
                    ? 'Defaults to the file name'
                    : `Defaults to "${typeMeta?.label}"`
                }
                maxLength={120}
                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
              />
              <SourceToggle
                mode={item.mode}
                onChange={(mode) => {
                  setFileError(item.id, null);
                  update(item.id, { mode });
                }}
              />
            </div>

            {item.mode === 'file' ? (
              <FileDropZone
                label=""
                accept=".pdf,.doc,.docx,.txt"
                file={item.file}
                onFile={(f) => onFile(item.id, f)}
                hint="PDF, DOC or TXT, up to 10 MB."
              />
            ) : (
              <div className="space-y-1">
                <textarea
                  rows={4}
                  value={item.text}
                  maxLength={maxTextChars}
                  onChange={(e) => update(item.id, { text: e.target.value })}
                  aria-label={`Content for ${itemLabel}`}
                  placeholder={typeMeta?.placeholder}
                  className="w-full resize-y rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
                />
                <p
                  className={cn(
                    'text-right text-[11px] tabular-nums',
                    item.text.length >= maxTextChars ? 'text-neurax-amber' : 'text-muted-foreground'
                  )}
                >
                  {item.text.length.toLocaleString('en-US')} /{' '}
                  {maxTextChars.toLocaleString('en-US')}
                </p>
              </div>
            )}

            {fileError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              >
                <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{fileError}</span>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        disabled={atLimit}
        onClick={() => onChange([...items, createAdditionalMaterialDraft()])}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-3 text-sm font-medium text-muted-foreground transition hover:border-neurax-electric/40 hover:bg-neurax-electric/[0.04] hover:text-neurax-electric disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-white/10 disabled:hover:bg-white/[0.02] disabled:hover:text-muted-foreground"
      >
        {atLimit ? (
          `Limit reached (${maxItems} max)`
        ) : (
          <>
            <Plus className="h-4 w-4" />
            Add material
          </>
        )}
      </button>
    </div>
  );
}
